import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../Components/AuthContext';
import { ArrowLeft, Package, Plus, CheckCircle, XCircle, Send } from 'lucide-react';
import MainLayout from '../../Components/Layout/MainLayout/MainLayout';
import { Button } from '../../Components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../../Components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetBody, SheetFooter } from '../../Components/ui/sheet';

const inputClass = "h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50";

const FLAG_LABEL = { solo: 'Solo', duo: 'Duo', home_gym: 'Home Gym', referral: 'Referral' };
const DISCOUNT_TYPE_LABEL = { frequency: 'Frequency', home_gym: 'Home Gym', referral: 'Referral' };

const STATUS_BADGE = {
  active:    'bg-green-100 text-green-700',
  exhausted: 'bg-yellow-100 text-yellow-700',
  expired:   'bg-muted text-muted-foreground',
  cancelled: 'bg-muted text-muted-foreground',
};

const INVOICE_STATUS_BADGE = {
  draft: 'bg-yellow-100 text-yellow-700',
  sent:  'bg-blue-100 text-blue-700',
  paid:  'bg-green-100 text-green-700',
  void:  'bg-muted text-muted-foreground',
};

const ClientDetail = () => {
  const { tenantDomain, clientId } = useParams();
  const { updateTenantDomain } = useAuth();
  const navigate = useNavigate();

  const [client, setClient] = useState(null);
  const [clientPackages, setClientPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Assign package sheet
  const [sheetOpen, setSheetOpen] = useState(false);
  const [availablePackages, setAvailablePackages] = useState([]);
  const [selectedPackageId, setSelectedPackageId] = useState('');
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [flags, setFlags] = useState({ booking: '', home_gym: false, referral: false });
  const [pricePreview, setPricePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  // Void invoice dialog
  const [voidTarget, setVoidTarget] = useState(null);
  const [voiding, setVoiding] = useState(false);

  const fetchClient = useCallback(async () => {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/${tenantDomain}/client/${clientId}`, {
      credentials: 'include',
    });
    if (!response.ok) throw new Error(`${response.status}`);
    return response.json();
  }, [tenantDomain, clientId]);

  const fetchClientPackages = useCallback(async () => {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/${tenantDomain}/client/${clientId}/package`, {
      credentials: 'include',
    });
    if (!response.ok) throw new Error(`${response.status}`);
    return response.json();
  }, [tenantDomain, clientId]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [c, cp] = await Promise.all([fetchClient(), fetchClientPackages()]);
      setClient(c);
      setClientPackages(cp);
    } catch (err) {
      setError('Failed to load client. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [fetchClient, fetchClientPackages]);

  useEffect(() => {
    updateTenantDomain(tenantDomain);
    load();
  }, [tenantDomain, clientId]);

  const openAssignSheet = async () => {
    setSelectedPackageId('');
    setSelectedPackage(null);
    setFlags({ booking: '', home_gym: false, referral: false });
    setPricePreview(null);
    setFormError(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/${tenantDomain}/package`, {
        credentials: 'include',
      });
      const data = await response.json();
      setAvailablePackages(data.filter(p => p.isActive));
    } catch {
      setAvailablePackages([]);
    }
    setSheetOpen(true);
  };

  const handlePackageSelect = (pkgId) => {
    setSelectedPackageId(pkgId);
    const pkg = availablePackages.find(p => p.packageId === pkgId) ?? null;
    setSelectedPackage(pkg);
    setFlags({ booking: '', home_gym: false, referral: false });
    if (pkg) computePreview(pkg, { booking: '', home_gym: false, referral: false });
    else setPricePreview(null);
  };

  const handleFlagChange = (updatedFlags) => {
    setFlags(updatedFlags);
    if (selectedPackage) computePreview(selectedPackage, updatedFlags);
  };

  const computePreview = (pkg, currentFlags) => {
    const applicable = (pkg.discounts ?? []).filter(d => {
      if (d.discountType === 'frequency') return pkg.sessionsPerWeek != null;
      if (d.discountType === 'home_gym') return currentFlags.home_gym;
      if (d.discountType === 'referral') return currentFlags.referral;
      return false;
    });
    const totalDiscount = applicable.reduce((sum, d) => sum + d.discountValue, 0);
    const effectivePrice = pkg.basePricePerSession - totalDiscount;
    const total = effectivePrice * pkg.sessionCredits;
    setPricePreview({ applicable, effectivePrice, total });
  };

  const handleAssign = async () => {
    if (!selectedPackageId) {
      setFormError('Please select a package.');
      return;
    }
    const flagList = [];
    if (flags.booking) flagList.push(flags.booking);
    if (flags.home_gym) flagList.push('home_gym');
    if (flags.referral) flagList.push('referral');

    setSaving(true);
    setFormError(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/${tenantDomain}/client/${clientId}/package`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ packageId: selectedPackageId, flags: flagList }),
        }
      );
      if (!response.ok) throw new Error(`${response.status}`);
      await load();
      setSheetOpen(false);
    } catch (err) {
      setFormError('Failed to assign package. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSendInvoice = async (invoice) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/${tenantDomain}/invoice/${invoice.invoiceId}/send`,
        { method: 'POST', credentials: 'include' }
      );
      if (response.status === 422) {
        const text = await response.text();
        if (text === 'StripeNotConnected') setError('Connect your Stripe account in Settings before sending invoices.');
        else if (text === 'ClientHasNoEmail') setError('This client has no email address on file.');
        else setError('Failed to send invoice.');
        return;
      }
      if (!response.ok) throw new Error(`${response.status}`);
      await load();
    } catch {
      setError('Failed to send invoice. Please try again.');
    }
  };

  const handleMarkPaid = async (invoice) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/${tenantDomain}/invoice/${invoice.invoiceId}/pay`,
        { method: 'PUT', credentials: 'include' }
      );
      if (!response.ok) throw new Error(`${response.status}`);
      await load();
    } catch {
      setError('Failed to mark invoice as paid. Please try again.');
    }
  };

  const handleVoid = async () => {
    setVoiding(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/${tenantDomain}/invoice/${voidTarget.invoiceId}/void`,
        { method: 'PUT', credentials: 'include' }
      );
      if (!response.ok) throw new Error(`${response.status}`);
      await load();
      setVoidTarget(null);
    } catch {
      setError('Failed to void invoice. Please try again.');
    } finally {
      setVoiding(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="text-sm text-muted-foreground py-8">Loading...</div>
      </MainLayout>
    );
  }

  if (!client) {
    return (
      <MainLayout>
        <div className="text-sm text-muted-foreground py-8">Client not found.</div>
      </MainLayout>
    );
  }

  const activePackages = clientPackages.filter(cp => cp.status === 'active');
  const pastPackages = clientPackages.filter(cp => cp.status !== 'active');

  return (
    <MainLayout>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/${tenantDomain}/clients`)}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
        >
          <ArrowLeft size={14} /> Back to Clients
        </button>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">{client.firstName} {client.lastName}</h1>
            <p className="text-sm text-muted-foreground mt-0.5">{client.email}</p>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {!client.isActive && (
                <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-muted text-muted-foreground">Inactive</span>
              )}
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${client.userActive ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                {client.userActive ? 'Account active' : 'Pending setup'}
              </span>
              {client.phone && <span className="text-sm text-muted-foreground">{client.phone}</span>}
            </div>
          </div>
          <Button onClick={openAssignSheet}>
            <Plus size={15} className="mr-1.5" /> Assign Package
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">{error}</div>
      )}

      {/* Active packages */}
      <section className="mb-8">
        <h2 className="text-base font-semibold mb-3">Active Packages</h2>
        {activePackages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center rounded-lg border border-dashed">
            <Package size={28} className="text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">No active packages. Assign one to get started.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {activePackages.map(cp => (
              <ClientPackageCard
                key={cp.clientPackageId}
                cp={cp}
                onSendInvoice={handleSendInvoice}
                onMarkPaid={handleMarkPaid}
                onVoid={setVoidTarget}
              />
            ))}
          </div>
        )}
      </section>

      {/* Past packages */}
      {pastPackages.length > 0 && (
        <section>
          <h2 className="text-base font-semibold mb-3 text-muted-foreground">Past Packages</h2>
          <div className="flex flex-col gap-3">
            {pastPackages.map(cp => (
              <ClientPackageCard
                key={cp.clientPackageId}
                cp={cp}
                onSendInvoice={handleSendInvoice}
                onMarkPaid={handleMarkPaid}
                onVoid={setVoidTarget}
                past
              />
            ))}
          </div>
        </section>
      )}

      {/* Assign Package Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Assign Package</SheetTitle>
          </SheetHeader>
          <SheetBody>
            <div className="flex flex-col gap-4">
              {formError && (
                <div className="rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">{formError}</div>
              )}

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Package <span className="text-destructive">*</span></label>
                <select
                  value={selectedPackageId}
                  onChange={(e) => handlePackageSelect(e.target.value)}
                  className={inputClass}
                >
                  <option value="">Select a package...</option>
                  {availablePackages.map(p => (
                    <option key={p.packageId} value={p.packageId}>
                      {p.name} — {p.sessionCredits} credits @ ${p.basePricePerSession.toFixed(2)}/session
                    </option>
                  ))}
                </select>
              </div>

              {selectedPackage && (
                <>
                  {selectedPackage.sessionsPerWeek != null && (
                    <p className="text-xs text-muted-foreground">
                      Frequency discount applies automatically ({selectedPackage.sessionsPerWeek}x/week).
                    </p>
                  )}

                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium">Session type</label>
                    <div className="flex gap-3">
                      {['solo', 'duo'].map(type => (
                        <label key={type} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="booking-type"
                            value={type}
                            checked={flags.booking === type}
                            onChange={() => handleFlagChange({ ...flags, booking: type })}
                            className="h-4 w-4"
                          />
                          <span className="text-sm">{FLAG_LABEL[type]}</span>
                        </label>
                      ))}
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="booking-type"
                          value=""
                          checked={flags.booking === ''}
                          onChange={() => handleFlagChange({ ...flags, booking: '' })}
                          className="h-4 w-4"
                        />
                        <span className="text-sm text-muted-foreground">None</span>
                      </label>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium">Discount qualifiers</label>
                    {['home_gym', 'referral'].map(flag => (
                      <label key={flag} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={flags[flag]}
                          onChange={(e) => handleFlagChange({ ...flags, [flag]: e.target.checked })}
                          className="h-4 w-4 rounded border-input"
                        />
                        <span className="text-sm">{FLAG_LABEL[flag]}</span>
                      </label>
                    ))}
                  </div>

                  {pricePreview && (
                    <div className="rounded-md border bg-muted/20 p-4 flex flex-col gap-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Price breakdown</p>
                      <div className="flex justify-between text-sm">
                        <span>Base price/session</span>
                        <span>${selectedPackage.basePricePerSession.toFixed(2)}</span>
                      </div>
                      {pricePreview.applicable.map((d, i) => (
                        <div key={i} className="flex justify-between text-sm text-green-700">
                          <span>{d.name} ({DISCOUNT_TYPE_LABEL[d.discountType]})</span>
                          <span>−${d.discountValue.toFixed(2)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between text-sm font-medium border-t pt-2 mt-1">
                        <span>Effective price/session</span>
                        <span>${pricePreview.effectivePrice.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm font-semibold">
                        <span>Invoice total ({selectedPackage.sessionCredits} sessions)</span>
                        <span>${pricePreview.total.toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </SheetBody>
          <SheetFooter>
            <Button variant="outline" onClick={() => setSheetOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleAssign} disabled={saving || !selectedPackageId}>
              {saving ? 'Assigning...' : 'Assign & Invoice'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Void Invoice Dialog */}
      <Dialog open={!!voidTarget} onOpenChange={(open) => { if (!open) setVoidTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Void Invoice</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-muted-foreground">
              Are you sure you want to void this invoice for <strong>${voidTarget?.amount?.toFixed(2)}</strong>?
              This cannot be undone.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoidTarget(null)} disabled={voiding}>Cancel</Button>
            <Button
              onClick={handleVoid}
              disabled={voiding}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {voiding ? 'Voiding...' : 'Void Invoice'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

const ClientPackageCard = ({ cp, onSendInvoice, onMarkPaid, onVoid, past }) => (
  <div className={`rounded-lg border bg-card p-5 flex flex-col gap-3 ${past ? 'opacity-60' : ''}`}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-sm font-semibold">{cp.packageName ?? 'Package'}</p>
        <div className="flex flex-wrap gap-1 mt-1">
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[cp.status]}`}>
            {cp.status.charAt(0).toUpperCase() + cp.status.slice(1)}
          </span>
          {(cp.flags ?? []).map(f => (
            <span key={f} className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-muted text-muted-foreground">
              {FLAG_LABEL[f] ?? f}
            </span>
          ))}
        </div>
      </div>
      <div className="text-right shrink-0">
        <p className="text-sm font-semibold">{cp.creditsRemaining} / {cp.creditsTotal} credits</p>
        {cp.expiresAt && (
          <p className="text-xs text-muted-foreground mt-0.5">
            Expires {new Date(cp.expiresAt).toLocaleDateString()}
          </p>
        )}
      </div>
    </div>

    {/* Credits bar */}
    <div className="w-full bg-muted rounded-full h-1.5">
      <div
        className="bg-primary h-1.5 rounded-full transition-all"
        style={{ width: `${cp.creditsTotal > 0 ? (cp.creditsRemaining / cp.creditsTotal) * 100 : 0}%` }}
      />
    </div>

    {/* Invoice */}
    {cp.invoice && (
      <div className="flex items-center justify-between pt-2 border-t gap-3">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${INVOICE_STATUS_BADGE[cp.invoice.status]}`}>
            Invoice {cp.invoice.status}
          </span>
          <span className="text-sm font-medium">${cp.invoice.amount.toFixed(2)}</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {(cp.invoice.status === 'draft' || cp.invoice.status === 'sent') && (
            <>
              <Button size="sm" onClick={() => onSendInvoice(cp.invoice)}>
                <Send size={13} className="mr-1.5" />
                {cp.invoice.status === 'sent' ? 'Resend Link' : 'Send Invoice'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onMarkPaid(cp.invoice)}
              >
                <CheckCircle size={13} className="mr-1.5" /> Mark Paid Offline
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onVoid(cp.invoice)}
                className="border-destructive/40 text-destructive hover:bg-destructive/10"
              >
                <XCircle size={13} className="mr-1.5" /> Void
              </Button>
            </>
          )}
          {cp.invoice.status === 'paid' && cp.invoice.paidAt && (
            <span className="text-xs text-muted-foreground">
              Paid {new Date(cp.invoice.paidAt).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>
    )}
  </div>
);

export default ClientDetail;
